import type { PowerConsumptionPlan } from "../../types/solar";
import type { ErrorSeverity } from "../../types/error";
import { DEFAULT_ERROR_SEVERITY } from "../../types/error";

/**
 * EnterDetailsStep State Machine
 * 
 * Manages the lifecycle of the details entry step:
 * 1. Select power consumption estimate
 * 2. Load/Create project
 * 3. Poll until terminal state (COMPLETED/ERROR/TIMEOUT)
 * 4. Display results
 */

export type EnterDetailsStepState = {
  selectedPowerConsumption: PowerConsumptionPlan;
  isLoading: boolean;
  isPolling: boolean;
  error: string | null;
  errorSeverity?: ErrorSeverity;
  pollingResult: "COMPLETED" | "ERROR" | "TIMEOUT" | null;
};

export type EnterDetailsStepAction =
  | {
      type: "SELECT_POWER_CONSUMPTION";
      payload: PowerConsumptionPlan;
    }
  | {
      type: "START_LOADING";
    }
  | {
      type: "START_POLLING";
    }
  | {
      type: "POLLING_COMPLETED";
    }
  | {
      type: "POLLING_ERROR";
      payload: string;
      severity?: ErrorSeverity;
    }
  | {
      type: "POLLING_TIMEOUT";
    }
  | {
      type: "LOADING_ERROR";
      payload: string;
      severity?: ErrorSeverity;
    }
  | {
      type: "RESET_ERROR";
    }
  | {
      type: "RESET";
      payload: PowerConsumptionPlan;
    };

export const initialEnterDetailsStepState = (
  selectedPowerConsumption: PowerConsumptionPlan
): EnterDetailsStepState => ({
  selectedPowerConsumption,
  isLoading: false,
  isPolling: false,
  error: null,
  errorSeverity: DEFAULT_ERROR_SEVERITY,
  pollingResult: null,
});

/**
 * Reducer for EnterDetailsStep state management
 * 
 * State transitions:
 * - SELECT_POWER_CONSUMPTION: User selects household size
 * - START_LOADING: Begin project load/create
 * - START_POLLING: Begin polling for project completion
 * - POLLING_COMPLETED: Project generation complete
 * - POLLING_ERROR: Project generation error
 * - POLLING_TIMEOUT: Polling exceeded 3-minute limit
 * - LOADING_ERROR: Error during load/create phase
 * - RESET_ERROR: Clear error message
 * - RESET: Reset to initial state with new selection
 */
export const enterDetailsStepReducer = (
  state: EnterDetailsStepState,
  action: EnterDetailsStepAction
): EnterDetailsStepState => {
  switch (action.type) {
    case "SELECT_POWER_CONSUMPTION":
      return {
        ...state,
        selectedPowerConsumption: action.payload,
        error: null,
      };

    case "START_LOADING":
      return {
        ...state,
        isLoading: true,
        error: null,
        pollingResult: null,
      };

    case "LOADING_ERROR":
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        errorSeverity: action.severity ?? DEFAULT_ERROR_SEVERITY,
      };

    case "START_POLLING":
      return {
        ...state,
        isLoading: false,
        isPolling: true,
        error: null,
      };

    case "POLLING_COMPLETED":
      return {
        ...state,
        isPolling: false,
        pollingResult: "COMPLETED",
        error: null,
      };

    case "POLLING_ERROR":
      return {
        ...state,
        isPolling: false,
        pollingResult: "ERROR",
        error: action.payload,
        errorSeverity: action.severity ?? DEFAULT_ERROR_SEVERITY,
      };

    case "POLLING_TIMEOUT":
      return {
        ...state,
        isPolling: false,
        pollingResult: "TIMEOUT",
        error:
          "Die Verarbeitung dauert länger als erwartet. Bitte versuchen Sie es in ein paar Minuten erneut.",
      };

    case "RESET_ERROR":
      return {
        ...state,
        error: null,
      };

    case "RESET":
      return initialEnterDetailsStepState(action.payload);

    default:
      return state;
  }
};
